import { useEffect, useRef, useState, type ReactNode } from "react";
// Link, НЕ NavLink — див. коментар у Sidebar.tsx (aria-current на двох пунктах).
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, CircleAlert, Eye, HelpCircle, Lock, LogOut, Users, X } from "lucide-react";
import { useAuthStore } from "@/store/useAuthStore";
import { useNavBadges } from "@/hooks/useNavBadges";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { buildNavModel, navActiveRoute } from "@/lib/navRegistry";
import { NavBadge, navItemAriaLabel } from "./Sidebar";
import { KormaxAvaLogo } from "@/components/brand/KormaxAvaLogo";
import { logout as apiLogout } from "@/api/auth";
import { cn } from "@/lib/cn";
import type { NavBadgeScope } from "@/api/types";

/**
 * D-124 M1 — mobile navigation shell (off-canvas drawer, <768px only).
 *
 * 🔴 Головне правило: renders the SAME backend `navModel` the desktop Sidebar
 * uses — `buildNavModel(allowedRoutes)` + `useNavBadges` (identical badge truth)
 * + the exported `NavBadge`. ZERO frontend role-inference: visibility / labels /
 * read-only / badges / admin-section / scope all come from the backend contract.
 * Никакого fallback-меню «всім» — недоступний navModel → error/empty, не дефолт.
 *
 * Behavior (TZ §7, iOS Safari critical): close via scrim / ✕ / item-select / Esc
 * / browser-Back; body scroll-lock + focus-trap + restore-focus→hamburger
 * (useDrawerA11y); background `inert` set by AppLayout; safe-area insets.
 */
export function MobileNavDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const allowedRoutes = useAuthStore((s) => s.allowedRoutes);
  const user = useAuthStore((s) => s.user);
  const resetSession = useAuthStore((s) => s.reset);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { badgeCount } = useNavBadges(allowedRoutes);
  // Hook must sit ABOVE the `if (!open) return null` early-return below.
  const { pathname } = useLocation();
  const panelRef = useDrawerA11y(open, onClose); // Esc + focus-trap + scroll-lock + restore-focus

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Entry slide-in (mount → next frame → translate). prefers-reduced-motion =
  // instant via the motion-reduce:duration-0 utility below.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Browser Back closes the drawer (NOT a route navigation): push a throwaway
  // history entry on open; popstate → close; closing via UI pops it back.
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ kormaxNavDrawer: true }, "");
    const onPop = () => onCloseRef.current();
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (window.history.state?.kormaxNavDrawer) window.history.back();
    };
  }, [open]);

  // If the viewport grows to ≥768px while open, hand back to the desktop sidebar.
  useEffect(() => {
    if (!open) return;
    const mql = window.matchMedia("(min-width: 768px)");
    const onChange = () => {
      if (mql.matches) onCloseRef.current();
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [open]);

  if (!open) return null;

  const sections = buildNavModel(allowedRoutes);
  // AC-W11-ACTIVE-ROUTE: identical longest-route-wins resolution as the desktop
  // Sidebar — same helper, same result on /knowledge vs /knowledge/admin.
  const activeRoute = navActiveRoute(sections, pathname);
  // Scope chip + read-only marker — derived from BACKEND data (badgeScope /
  // accessMode), not inferred from the role name.
  const scope = allowedRoutes.find((r) => r.accessMode !== "hidden" && r.badgeScope)?.badgeScope;
  const isReadOnly = sections.some((s) => s.items.some((i) => i.readOnly));
  // «Допомога» (AI-помічник) gated: visible only when a dedicated help route is
  // live + allowed (omit-based backend contract). NB: «База знань» (KB) is its
  // OWN nav item in «Підтримка» — «Допомога» is the AI assistant, whose mobile
  // entry is a later slice (M4), so the backend exposes no `ai_help` route yet →
  // hidden, never a dead CTA (AC-MOBILE-HELP, §20).
  const helpAllowed = allowedRoutes.some(
    (r) => r.routeKey === "ai_help" && r.accessMode !== "hidden",
  );
  const roleLabel = user?.roles?.length ? user.roles.join(", ") : null;

  const doLogout = async () => {
    try {
      await apiLogout();
    } catch {
      /* network logout best-effort; clear locally regardless */
    }
    resetSession();
    qc.removeQueries({ queryKey: ["me"] });
    navigate("/login", { replace: true });
  };

  return (
    <div className="fixed inset-0 z-40 md:hidden" role="presentation">
      {/* scrim (z-40) */}
      <div
        className={cn(
          "absolute inset-0 bg-[rgba(15,23,42,0.5)] transition-opacity duration-[220ms] ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none motion-reduce:duration-0",
          entered ? "opacity-100" : "opacity-0",
        )}
        onClick={onClose}
        aria-hidden
      />

      {/* panel (z-50) */}
      <div
        ref={panelRef}
        id="mobile-nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Навігація"
        className={cn(
          "absolute left-0 top-0 z-50 flex h-full w-[min(86vw,320px)] flex-col bg-card text-foreground shadow-xl outline-none",
          "pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]",
          "transition-transform duration-[220ms] ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none motion-reduce:duration-0",
          entered ? "translate-x-0" : "-translate-x-full",
        )}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        {/* header — real AVA mark (AC-MOBILE-BRAND) */}
        <div className="flex items-center gap-3 border-b px-3.5 py-3">
          <KormaxAvaLogo className="h-8 w-8 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-bold leading-tight">Kormax CRM</div>
            <div className="truncate text-[11.5px] text-muted-foreground">
              {user?.name ?? ""}
              {roleLabel ? ` · ${roleLabel}` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити меню"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        {!user ? (
          <DrawerLoading />
        ) : allowedRoutes.length === 0 ? (
          <DrawerCentered
            icon={<CircleAlert className="h-6 w-6 text-destructive" />}
            title="Не вдалося завантажити меню"
            subtitle="Перевірте зв'язок"
            action={{ label: "Спробувати ще", onClick: () => window.location.reload() }}
          />
        ) : sections.length === 0 ? (
          <DrawerCentered
            icon={<Lock className="h-6 w-6 text-muted-foreground" />}
            title="Немає доступних розділів"
            action={{ label: "Вийти", onClick: doLogout }}
          />
        ) : (
          <>
            {(scope || isReadOnly) && (
              <div className="flex flex-wrap items-center gap-2 px-3.5 pt-3">
                {scope && <ScopeChip scope={scope} />}
                {isReadOnly && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-warning px-2 py-1 text-[11px] font-semibold text-warning-foreground">
                    <Eye className="h-3 w-3" /> Лише перегляд
                  </span>
                )}
              </div>
            )}

            <nav
              role="navigation"
              aria-label="Основна навігація"
              className="flex-1 overflow-y-auto px-2 py-2"
            >
              {sections.map(({ section, items }) => (
                <div key={section} className="mb-1.5">
                  <div className="px-2.5 pb-1 pt-2.5 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
                    {section}
                  </div>
                  <div className="space-y-0.5">
                    {items.map((item) => {
                      const Icon = item.icon;
                      const count = badgeCount(item.badgeBinding);
                      const isActive = item.route === activeRoute;
                      return (
                        <Link
                          key={item.routeKey}
                          to={item.to}
                          onClick={onClose}
                          aria-label={navItemAriaLabel(item, count)}
                          aria-current={isActive ? "page" : undefined}
                          className={cn(
                            "group relative flex min-h-[46px] items-center gap-3 rounded-lg px-2.5 text-[13.5px] transition-colors",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            isActive
                              ? "bg-primary-soft font-semibold text-primary-dark"
                              : "text-foreground/80 hover:bg-accent hover:text-foreground",
                          )}
                        >
                          <>
                              {isActive && (
                                <span
                                  aria-hidden
                                  className="absolute -left-2 top-2 bottom-2 w-[3px] rounded-r bg-primary"
                                />
                              )}
                              <Icon
                                className={cn(
                                  "h-[19px] w-[19px] shrink-0",
                                  isActive ? "text-primary" : "text-muted-foreground",
                                )}
                                strokeWidth={2}
                              />
                              <span className="flex-1 truncate text-left">{item.label}</span>
                              {item.placeholderNote && (
                                <span className="shrink-0 rounded border border-border bg-background px-1 text-[9.5px] font-semibold text-muted-foreground">
                                  {item.placeholderNote}
                                </span>
                              )}
                              {item.readOnly && (
                                <Lock
                                  aria-hidden
                                  className="h-[13px] w-[13px] shrink-0 text-warning-foreground"
                                />
                              )}
                              {count !== undefined && (
                                <NavBadge count={count} style={item.badgeStyle} scope={item.badgeScope} />
                              )}
                          </>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            <div className="border-t px-2 py-2">
              {helpAllowed && (
                <button
                  type="button"
                  className="flex min-h-[46px] w-full items-center gap-3 rounded-lg px-2.5 text-[13.5px] text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <HelpCircle className="h-[19px] w-[19px]" /> Допомога
                </button>
              )}
              <button
                type="button"
                onClick={doLogout}
                className="flex min-h-[46px] w-full items-center gap-3 rounded-lg px-2.5 text-[13.5px] text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <LogOut className="h-[19px] w-[19px]" /> Вийти
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ScopeChip({ scope }: { scope: NavBadgeScope }) {
  const map = {
    my: { label: "Мої клієнти", Icon: Users, cls: "bg-primary-soft text-primary-dark" },
    team: { label: "Команда", Icon: Users, cls: "bg-primary-soft text-primary-dark" },
    all: { label: "Вся компанія", Icon: Building2, cls: "bg-info text-info-foreground" },
  } as const;
  const { label, Icon, cls } = map[scope];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold",
        cls,
      )}
    >
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

function DrawerLoading() {
  return (
    <div className="flex-1 px-3.5 py-4" aria-busy="true" aria-label="Завантаження меню">
      <div className="mb-3 h-3 w-2/5 animate-pulse rounded bg-muted" />
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="my-2 h-[18px] w-4/5 animate-pulse rounded bg-muted" />
      ))}
    </div>
  );
}

function DrawerCentered({
  icon,
  title,
  subtitle,
  action,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  action: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted">{icon}</div>
      <div className="text-sm font-semibold">{title}</div>
      {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
      <button
        type="button"
        onClick={action.onClick}
        className="mt-1.5 inline-flex min-h-[44px] items-center rounded-lg border px-4 text-sm font-semibold hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {action.label}
      </button>
    </div>
  );
}
